/**
 * Context Menu
 *
 * Icon-only popup that appears next to the cursor when a selection completes
 * (click that lands on geometry, or box-drag end with non-empty selection).
 * Anchored to a **world-space** point so it sticks to its origin as the camera
 * pans/zooms — re-projects to screen each frame.
 *
 * Tools own one of these per session/edit mode and show/hide it whenever the
 * selection changes. Items are rebuilt per `show()` based on what's selected,
 * so the menu always reflects "what can I do with this selection right now."
 *
 * Originally lived next to the skeleton-editor session as
 * `WallSketchEditContextMenu`; lifted out here because the same shape is reused
 * by the door/window tool. The class is fully agnostic to selection type — it
 * just consumes a `ContextMenuItem[]`.
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';

export interface ContextMenuItem {
  /** Stable id, used for testing / future shortcuts. */
  id: string;
  /**
   * Inline SVG markup for the icon. Provided as a string so the menu can
   * stay React-free (it lives in vanilla DOM, parented to the renderArea).
   * Exported icon constants below match the HeroIcons 24-outline style; we
   * can swap them for proper React-rendered HeroIcons later when this menu
   * is folded into a React component.
   */
  iconSvg: string;
  /** Hover tooltip text (browser-native title attribute). */
  tooltip: string;
  /** Click handler. */
  onClick: () => void;
  /**
   * Whether clicking this item should auto-close the menu. Default: false —
   * the user typically wants to keep working with the same selection.
   * Destructive items (Delete) opt in by setting this to true (no selection
   * remains after deletion, so leaving the menu around would be misleading).
   */
  closeOnClick?: boolean;
}

// HeroIcons 24-outline style SVG markup, inlined so the menu can render
// them in vanilla DOM. The viewBox + stroke conventions match heroicons.
const SVG_BASE = 'fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" xmlns="http://www.w3.org/2000/svg"';

export const ICON_TRASH_SVG = `<svg ${SVG_BASE} width="18" height="18">
  <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
</svg>`;

export const ICON_REFRESH_SVG = `<svg ${SVG_BASE} width="18" height="18">
  <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
</svg>`;

// Ruler / measure
export const ICON_RULER_SVG = `<svg ${SVG_BASE} width="18" height="18">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m-17.25 0h17.25M3.375 4.5h17.25M3.375 6.375v3M3.375 12v3M9 6.375v3M9 12v3M14.625 6.375v3M14.625 12v3M20.25 6.375v3M20.25 12v3" />
</svg>`;

// Plus — used by "place door/window on edge" actions in the door/window tool.
export const ICON_PLUS_SVG = `<svg ${SVG_BASE} width="18" height="18">
  <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
</svg>`;

// Right angle — an L-shaped corner with a small square marking the 90°, used by
// the "orthogonalize (snap near-90° corners to 90°)" action.
export const ICON_RIGHT_ANGLE_SVG = `<svg ${SVG_BASE} width="18" height="18">
  <path stroke-linecap="round" stroke-linejoin="round" d="M5.25 4.5v14.25H19.5" />
  <path stroke-linecap="round" stroke-linejoin="round" d="M5.25 14.25h4.5v4.5" />
</svg>`;

// Parallel — two slanted parallel strokes, used by the "make parallel (align
// selected walls to one direction)" action.
export const ICON_PARALLEL_SVG = `<svg ${SVG_BASE} width="18" height="18">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9 4.5 5.25 19.5" />
  <path stroke-linecap="round" stroke-linejoin="round" d="M18.75 4.5 15 19.5" />
</svg>`;

// Door — the app's own door glyph (src/assets/icons/door.svg). Inlined as
// raw markup so the menu can render it in vanilla DOM without React /
// SVG-loader plumbing. Uses currentColor for fill/stroke so it picks up
// the menu button's text color (matches the other icons here).
export const ICON_DOOR_SVG = `<svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="fill-rule:evenodd;stroke:currentColor;clip-rule:evenodd;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:1.5;">
  <path d="M5.919,21.474l0,-14.477c0,-1.678 1.362,-3.041 3.041,-3.041l6.081,0c1.678,0 3.041,1.362 3.041,3.041l0,14.477" style="fill:none;stroke:currentColor;stroke-width:1.5px;" />
  <path d="M2.522,21.474l18.911,0" style="fill:none;stroke:currentColor;stroke-width:1.5px;" />
  <circle cx="8.69" cy="12.719" r="0.719" fill="currentColor" />
</svg>`;

// Window — the app's own window glyph (src/assets/icons/window.svg).
// Same inline-as-raw-markup convention as ICON_DOOR_SVG.
export const ICON_WINDOW_SVG = `<svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linecap:round;stroke-linejoin:round;">
  <g transform="matrix(0.660474,0,0,0.660474,3.937248,1.974969)">
    <path d="M12,3L12,21" style="fill:none;stroke:currentColor;stroke-width:2.27px;" />
  </g>
  <g transform="matrix(0.660474,0,0,0.660474,3.937248,1.974969)">
    <path d="M3,12L21,12" style="fill:none;stroke:currentColor;stroke-width:2.27px;" />
  </g>
  <g transform="matrix(0.660474,0,0,0.660474,4.604691,1.343299)">
    <path d="M20.405,8.456L20.405,17.456C20.405,19.94 18.388,21.956 15.905,21.956L6.489,21.956C4.006,21.956 1.989,19.94 1.989,17.456L1.989,8.456C1.989,5.973 4.006,3.956 6.489,3.956L15.905,3.956C18.388,3.956 20.405,5.973 20.405,8.456Z" style="fill:none;stroke:currentColor;stroke-width:2.27px;stroke-miterlimit:1.5;" />
  </g>
  <path d="M2.522,21.474L21.433,21.474" style="fill:none;stroke:currentColor;stroke-width:1.5px;stroke-miterlimit:1.5;" />
</svg>`;

const BUTTON_SIZE_PX = 32;
const BUTTON_GAP_PX = 4;
const MENU_OFFSET_PX = 12; // distance from anchor to top-left of menu

export class ContextMenu {
  private viewer: Viewer;
  private container: HTMLDivElement;
  private worldAnchor: THREE.Vector3 | null = null;
  private items: ContextMenuItem[] = [];

  // Bound listeners so we can attach/detach by reference.
  private readonly onUpdateBound: () => void;
  private readonly onDocumentClickBound: (e: MouseEvent) => void;
  private readonly onKeyDownBound: (e: KeyboardEvent) => void;

  constructor(viewer: Viewer) {
    this.viewer = viewer;

    this.container = document.createElement('div');
    this.container.style.cssText = `
      position: absolute;
      display: none;
      flex-direction: row;
      gap: ${BUTTON_GAP_PX}px;
      padding: 4px;
      background: rgba(20, 20, 20, 0.92);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      z-index: 10000;
      pointer-events: auto;
    `;

    // Append into the renderArea so it inherits the canvas's stacking context;
    // anchor coordinates are interpreted relative to the canvas's bounding rect.
    this.viewer.renderArea.appendChild(this.container);

    this.onUpdateBound = (): void => this.reprojectToScreen();
    this.onDocumentClickBound = (e: MouseEvent): void => this.handleDocumentClick(e);
    this.onKeyDownBound = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') this.hide();
    };
  }

  /**
   * The world-space anchor the menu is currently pinned to, or null when
   * the menu isn't shown. Exposed so callers that need to rebuild the
   * menu in place (e.g. after a click changed the selection state and
   * the available actions are now different) can re-`show()` against the
   * same anchor without losing the cursor position.
   */
  get currentAnchor(): THREE.Vector3 | null {
    return this.worldAnchor;
  }

  /**
   * Show the menu with `items`, anchored at `worldAnchor`. If already shown,
   * the position and items are updated in place (no flicker).
   */
  show(items: ContextMenuItem[], worldAnchor: THREE.Vector3): void {
    this.items = items;
    this.worldAnchor = worldAnchor.clone();
    this.renderItems();
    this.container.style.display = 'flex';
    this.reprojectToScreen();

    // Attach listeners (idempotent — viewer's EventDispatcher tolerates double-add).
    this.viewer.addEventListener('update', this.onUpdateBound);
    // Use capture-phase document-click so we close even if the click target
    // calls stopPropagation. Run on next tick so the click that opened the
    // menu doesn't immediately close it.
    setTimeout(() => {
      document.addEventListener('mousedown', this.onDocumentClickBound, true);
    }, 0);
    window.addEventListener('keydown', this.onKeyDownBound);
  }

  hide(): void {
    if (this.container.style.display === 'none') return;
    this.container.style.display = 'none';
    this.worldAnchor = null;
    this.items = [];
    this.viewer.removeEventListener('update', this.onUpdateBound);
    document.removeEventListener('mousedown', this.onDocumentClickBound, true);
    window.removeEventListener('keydown', this.onKeyDownBound);
  }

  isVisible(): boolean {
    return this.container.style.display !== 'none';
  }

  dispose(): void {
    this.hide();
    this.container.remove();
  }

  // ─── Internal ───────────────────────────────────────────────────────

  private renderItems(): void {
    this.container.innerHTML = '';
    for (const item of this.items) {
      const btn = document.createElement('button');
      btn.title = item.tooltip;
      btn.style.cssText = `
        width: ${BUTTON_SIZE_PX}px;
        height: ${BUTTON_SIZE_PX}px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        border-radius: 4px;
        color: rgba(255, 255, 255, 0.85);
        cursor: pointer;
        padding: 0;
      `;
      btn.addEventListener('mouseenter', () => {
        btn.style.background = 'rgba(255, 255, 255, 0.10)';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.background = 'transparent';
      });
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        // Most items keep the menu open after acting (the user is iterating
        // on the same selection — running recalc again, or returning from a
        // sub-mode). Items that explicitly opt into auto-close (e.g. Delete,
        // because the selection is gone afterwards) get hidden here.
        if (item.closeOnClick) {
          this.hide();
        }
        item.onClick();
      });

      const iconHost = document.createElement('span');
      iconHost.style.cssText = 'width: 18px; height: 18px; display: inline-flex; align-items: center; justify-content: center;';
      iconHost.innerHTML = item.iconSvg;
      btn.appendChild(iconHost);

      this.container.appendChild(btn);
    }
  }

  private reprojectToScreen(): void {
    if (!this.worldAnchor) return;
    const camera = this.viewer.scene.getActiveCamera();
    const size = this.viewer.renderer.getSize(new THREE.Vector2());
    const v = this.worldAnchor.clone().project(camera);
    // NDC [-1, 1] → canvas pixels.
    const canvasX = (v.x + 1) * 0.5 * size.width;
    const canvasY = (-v.y + 1) * 0.5 * size.height;

    // Clamp the menu so it stays inside the canvas rect even when the world
    // anchor is near (or off) the edge.
    const menuW = this.container.offsetWidth || (BUTTON_SIZE_PX * this.items.length + BUTTON_GAP_PX * (this.items.length - 1) + 8);
    const menuH = this.container.offsetHeight || BUTTON_SIZE_PX + 8;
    const left = clamp(canvasX + MENU_OFFSET_PX, 0, Math.max(0, size.width - menuW));
    const top = clamp(canvasY + MENU_OFFSET_PX, 0, Math.max(0, size.height - menuH));
    this.container.style.left = `${left}px`;
    this.container.style.top = `${top}px`;
  }

  private handleDocumentClick(e: MouseEvent): void {
    if (this.container.contains(e.target as Node)) return; // click inside menu — let item handlers run
    this.hide();
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
